import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail, Download, LogOut, RefreshCw, FileText, Gauge, CheckCircle2,
} from "lucide-react";
import Logo from "@/components/Logo";
import {
  adminStats, adminContacts, adminReadiness, downloadContactsCsv, clearToken,
} from "@/lib/adminAuth";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-stone-200 bg-[#FAFAF9] p-5">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">
        <Icon className="h-3.5 w-3.5 text-[#9C4221]" strokeWidth={2} /> {label}
      </div>
      <div className="mt-3 font-display text-4xl tracking-tighter font-bold text-stone-900 leading-none">
        {value}
      </div>
      {sub && <div className="mt-2 text-xs text-stone-500 leading-snug">{sub}</div>}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [readiness, setReadiness] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [s, c, r] = await Promise.all([adminStats(), adminContacts(), adminReadiness()]);
      setStats(s);
      setContacts(c.items || []);
      setReadiness(r.items || []);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        clearToken();
        navigate("/admin/login");
        return;
      }
      setError("Could not load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const handleExport = async () => {
    try {
      await downloadContactsCsv();
    } catch {
      setError("Could not export CSV.");
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-stone-200 bg-[#FAFAF9]/90 backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden md:inline-block text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] border border-[#9C4221]/30 bg-[#9C4221]/[0.06] px-2 py-1">
              Admin · Founder access
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 border border-stone-300 px-3 py-2 text-xs font-medium hover:border-stone-900 transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button onClick={handleLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 bg-stone-900 text-white px-3 py-2 text-xs font-medium hover:bg-stone-800 transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">Operating snapshot</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl tracking-tighter font-bold text-stone-900 leading-tight">
          Who's engaging with BIGIBILA — today.
        </h1>

        {error && (
          <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* STAT TILES */}
        <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
          <StatCard icon={Mail} label="Enquiries" value={loading ? "…" : stats?.contacts?.total ?? 0} sub={`${stats?.contacts?.emails_sent ?? 0} emails dispatched`} />
          <StatCard icon={Gauge} label="Readiness completions" value={loading ? "…" : stats?.readiness?.total ?? 0} sub="6-question self-assessment" />
          <StatCard icon={FileText} label="Capability downloads" value={loading ? "…" : stats?.capability_downloads ?? 0} sub="Procurement-ready PDF" />
          <StatCard icon={CheckCircle2} label="Pilot-ready leads" value={loading ? "…" : (stats?.readiness?.by_band?.["Pilot-ready"] ?? 0) + (stats?.readiness?.by_band?.["Enterprise-scale"] ?? 0)} sub="High-intent scoring" />
        </section>

        {/* READINESS BREAKDOWN */}
        {stats?.readiness && (
          <section className="mt-10">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500 mb-3">Readiness by band</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
              {Object.entries(stats.readiness.by_band).map(([band, count]) => (
                <div key={band} className="bg-[#FAFAF9] p-4">
                  <div className="text-xs text-stone-500 leading-snug">{band}</div>
                  <div className="mt-2 font-display text-2xl tracking-tight font-bold text-stone-900">{count}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CONTACT SUBMISSIONS */}
        <section className="mt-12">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">Inbox</div>
              <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900">Contact submissions ({contacts.length})</h2>
            </div>
            <button onClick={handleExport} data-testid="admin-export-csv" disabled={contacts.length === 0} className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 text-sm font-medium hover:border-stone-900 transition-colors disabled:opacity-40">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
          <div className="border border-stone-200 bg-[#FAFAF9] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500 bg-stone-100">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Org</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Subject</th>
                  <th className="text-left px-4 py-3">Message</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-500">No enquiries yet.</td></tr>
                )}
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-stone-200 hover:bg-stone-50 align-top" data-testid={`admin-contact-${c.id}`}>
                    <td className="px-4 py-3 text-stone-600 whitespace-nowrap">{fmtDate(c.submitted_at)}</td>
                    <td className="px-4 py-3 text-stone-900 font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-stone-700">{c.organisation || "—"}</td>
                    <td className="px-4 py-3"><a href={`mailto:${c.email}`} className="text-[#9C4221] hover:underline">{c.email}</a></td>
                    <td className="px-4 py-3 text-stone-700 max-w-xs truncate">{c.subject}</td>
                    <td className="px-4 py-3 text-stone-600 max-w-md text-xs whitespace-pre-wrap">{c.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* READINESS LOG */}
        <section className="mt-12">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">Diagnostic</div>
          <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900 mb-4">Readiness completions ({readiness.length})</h2>
          <div className="border border-stone-200 bg-[#FAFAF9] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500 bg-stone-100">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Score</th>
                  <th className="text-left px-4 py-3">Band</th>
                  <th className="text-left px-4 py-3">Recommended tier</th>
                </tr>
              </thead>
              <tbody>
                {readiness.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-stone-500">No completions yet.</td></tr>
                )}
                {readiness.map((r) => (
                  <tr key={r.id} className="border-t border-stone-200" data-testid={`admin-readiness-${r.id}`}>
                    <td className="px-4 py-3 text-stone-600 whitespace-nowrap">{fmtDate(r.submitted_at)}</td>
                    <td className="px-4 py-3 font-display font-bold text-stone-900">{r.score}/24</td>
                    <td className="px-4 py-3 text-stone-700">{r.band}</td>
                    <td className="px-4 py-3 text-stone-700">{r.tier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <p className="mt-12 text-[11px] text-stone-500 italic leading-relaxed border-t border-stone-200 pt-6">
          BIGIBILA SYSTEMS™ admin dashboard. Sessions persist for 24 hours from sign-in. All data shown is operational only — no community or cultural content is collected through this system.
        </p>
      </main>
    </div>
  );
}
